import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Search, ArrowLeft, BarChart3 } from 'lucide-react';
import Navigation from '@/components/Navigation';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: 'points', label: 'Points' },
  { key: 'rebounds', label: 'Rebounds' },
  { key: 'assists', label: 'Assists' },
  { key: 'steals', label: 'Steals' },
  { key: 'blocks', label: 'Blocks' },
];

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const g = payload[0].payload;
  return (
    <div className="bg-[#0a1628] border border-[#1e3a5f] rounded-md p-3 text-sm">
      <div className="text-white font-semibold">{g.home ? 'vs' : '@'} {label} <span className="text-muted-foreground">{g.date}</span></div>
      <div className="text-brand-cyan font-mono">{payload[0].value}</div>
    </div>
  );
};

const WnbaPlayers = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [team, setTeam] = useState('all');
  const [selected, setSelected] = useState(null);
  const [gamelog, setGamelog] = useState(null);
  const [logLoading, setLogLoading] = useState(false);
  const [category, setCategory] = useState(CATEGORIES[0]);

  useEffect(() => {
    axios.get(`${API}/wnba/players`)
      .then(r => setPlayers(r.data.players || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const selectPlayer = (p) => {
    setSelected(p);
    setGamelog(null);
    setCategory(CATEGORIES[0]);
    setLogLoading(true);
    axios.get(`${API}/wnba/player-gamelog`, { params: { name: p.name } })
      .then(r => setGamelog(r.data))
      .catch(console.error)
      .finally(() => setLogLoading(false));
  };

  const teams = useMemo(() => [...new Set(players.map(p => p.team).filter(Boolean))].sort(), [players]);

  const filtered = players.filter(p => {
    const mq = !query || p.name.toLowerCase().includes(query.toLowerCase());
    const mt = team === 'all' || p.team === team;
    return mq && mt;
  });

  const chartData = (gamelog?.games || []).map(g => ({ ...g, value: g[category.key] }));
  const avg = chartData.length ? (chartData.reduce((s, g) => s + g.value, 0) / chartData.length).toFixed(1) : '-';
  const high = chartData.length ? Math.max(...chartData.map(g => g.value)) : '-';
  const low = chartData.length ? Math.min(...chartData.map(g => g.value)) : '-';

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight flex items-center gap-3">
              <Users className="h-8 w-8 text-orange-400" />
              WNBA Players
            </h1>
            <p className="text-muted-foreground mt-1">Full league player directory</p>
          </div>

          {selected ? (
            <div className="space-y-4">
              <Button
                variant="outline"
                onClick={() => setSelected(null)}
                className="border-[#1e3a5f] text-white hover:bg-[#112240]"
                data-testid="wnba-back-to-players-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> All Players
              </Button>

              <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                <CardContent className="p-4 flex items-center gap-4">
                  {gamelog?.headshot && (
                    <img
                      src={gamelog.headshot}
                      alt={selected.name}
                      className="h-16 w-16 rounded-full bg-[#0a1628] object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div>
                    <div className="text-white text-xl font-bold" data-testid="wnba-selected-player-name">{selected.name}</div>
                    <div className="text-muted-foreground text-sm">{selected.team} · {selected.pos} · #{selected.num}</div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                <CardContent className="p-4 flex flex-wrap gap-2">
                  {CATEGORIES.map(cat => (
                    <Button
                      key={cat.key}
                      size="sm"
                      variant={category.key === cat.key ? 'default' : 'outline'}
                      onClick={() => setCategory(cat)}
                      className={category.key === cat.key
                        ? 'bg-brand-cyan text-[#0a1628] hover:bg-brand-cyan/90'
                        : 'border-[#1e3a5f] text-white hover:bg-[#112240]'}
                      data-testid={`wnba-stat-category-${cat.key}`}
                    >
                      {cat.label}
                    </Button>
                  ))}
                </CardContent>
              </Card>

              {logLoading ? (
                <div className="h-[300px] skeleton rounded" />
              ) : chartData.length === 0 ? (
                <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                  <CardContent className="p-8 text-center text-muted-foreground" data-testid="wnba-no-gamelog">
                    No recent game data available for this player.
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-[#0a1628] rounded-md p-4 text-center">
                          <div className="text-muted-foreground text-xs uppercase tracking-wide">L10 Avg</div>
                          <div className="text-2xl font-bold text-brand-cyan font-mono mt-1" data-testid="wnba-l10-avg">{avg}</div>
                        </div>
                        <div className="bg-[#0a1628] rounded-md p-4 text-center">
                          <div className="text-muted-foreground text-xs uppercase tracking-wide">High</div>
                          <div className="text-2xl font-bold text-brand-lime font-mono mt-1">{high}</div>
                        </div>
                        <div className="bg-[#0a1628] rounded-md p-4 text-center">
                          <div className="text-muted-foreground text-xs uppercase tracking-wide">Low</div>
                          <div className="text-2xl font-bold text-white font-mono mt-1">{low}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                    <CardHeader>
                      <CardTitle className="font-heading text-xl font-bold text-white uppercase tracking-wide flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-brand-lime" />
                        Last 10 Games - {category.label}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]" data-testid="wnba-gamelog-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chartData} barCategoryGap="20%" margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
                            <XAxis dataKey="opponent" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 11 }} interval={0} />
                            <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} allowDecimals={false} />
                            <Tooltip content={<ChartTooltip />} />
                            <Bar dataKey="value" radius={[4, 4, 0, 0]} animationDuration={500}>
                              {chartData.map((g, i) => (
                                <Cell key={i} fill={g.value >= avg ? '#8cc63f' : '#00a8e8'} />
                              ))}
                              <LabelList dataKey="value" position="top" fill="#ffffff" fontSize={13} fontWeight="bold" />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-4 flex items-center justify-center gap-6 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-sm bg-brand-lime" />
                          <span className="text-muted-foreground">At/Above L10 Avg</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-sm bg-brand-cyan" />
                          <span className="text-muted-foreground">Below L10 Avg</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                    <CardContent className="p-4 overflow-x-auto">
                      <table className="w-full text-sm" data-testid="wnba-gamelog-table">
                        <thead>
                          <tr className="text-muted-foreground text-[10px] uppercase border-b border-[#1e3a5f]">
                            <th className="text-left py-2 px-1">Date</th>
                            <th className="text-left py-2 px-1">Opp</th>
                            <th className="text-center py-2 px-1">Res</th>
                            <th className="text-center py-2 px-1">PTS</th>
                            <th className="text-center py-2 px-1">REB</th>
                            <th className="text-center py-2 px-1">AST</th>
                            <th className="text-center py-2 px-1">STL</th>
                            <th className="text-center py-2 px-1">BLK</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...chartData].reverse().map((g, i) => (
                            <tr key={i} className="border-b border-[#1e3a5f]/40">
                              <td className="py-2 px-1 text-muted-foreground">{g.date}</td>
                              <td className="py-2 px-1 text-white">{g.home ? 'vs' : '@'} {g.opponent}</td>
                              <td className={`text-center font-semibold ${g.result === 'W' ? 'text-brand-lime' : 'text-red-400'}`}>{g.result}</td>
                              <td className="text-center font-mono text-white">{g.points}</td>
                              <td className="text-center font-mono text-white">{g.rebounds}</td>
                              <td className="text-center font-mono text-white">{g.assists}</td>
                              <td className="text-center font-mono text-white">{g.steals}</td>
                              <td className="text-center font-mono text-white">{g.blocks}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search players..."
                    className="pl-9 bg-[#0f1e36] border-[#1e3a5f] text-white"
                    data-testid="wnba-player-search-input"
                  />
                </div>
                <Select value={team} onValueChange={(v) => { setTeam(v); setQuery(''); }}>
                  <SelectTrigger className="w-full sm:w-48 bg-[#0f1e36] border-[#1e3a5f] text-white" data-testid="wnba-team-filter-select">
                    <SelectValue placeholder="All Teams" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f1e36] border-[#1e3a5f]">
                    <SelectItem value="all" className="text-white hover:bg-[#112240]">All Teams</SelectItem>
                    {teams.map(t => (
                      <SelectItem key={t} value={t} className="text-white hover:bg-[#112240]">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-4">
                <Badge variant="outline" className="border-brand-cyan text-brand-cyan">{filtered.length} Players</Badge>
              </div>

              {loading ? (
                <div className="space-y-2">{[1, 2, 3, 4, 5].map(i => <div key={i} className="h-12 skeleton rounded" />)}</div>
              ) : (
                <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                  <CardContent className="p-4 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-muted-foreground text-[10px] uppercase border-b border-[#1e3a5f]">
                          <th className="text-left py-2 px-1">Player</th>
                          <th className="text-center py-2 px-1">Pos</th>
                          <th className="text-center py-2 px-1">Team</th>
                          <th className="text-center py-2 px-1">#</th>
                          <th className="text-center py-2 px-1">Height</th>
                          <th className="text-center py-2 px-1">Weight</th>
                          <th className="text-center py-2 px-1">Born</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((p, i) => (
                          <tr
                            key={i}
                            className="border-b border-[#1e3a5f]/40 table-row-hover cursor-pointer"
                            onClick={() => selectPlayer(p)}
                            data-testid={`wnba-player-row-${i}`}
                          >
                            <td className="py-2 px-1 text-brand-cyan font-medium hover:underline">{p.name}</td>
                            <td className="text-center text-muted-foreground">{p.pos}</td>
                            <td className="text-center"><span className="sport-icon sport-nba text-[9px] px-1.5 py-0.5">{p.team}</span></td>
                            <td className="text-center font-mono text-muted-foreground">{p.num}</td>
                            <td className="text-center text-muted-foreground">{p.height}</td>
                            <td className="text-center text-muted-foreground">{p.weight}</td>
                            <td className="text-center text-muted-foreground">{p.dob}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default WnbaPlayers;
